/* eslint-disable max-lines-per-function */
import React from "react";

import { Link } from "react-router-dom";
import { useFormikContext } from "formik";
import { Typography, Link as MuiLink, Grid } from "@mui/material";
import classNames from "classnames";
import { CheckboxWithErrorLabel } from "components/haven-design/form/checkbox";
import { Button } from "components/haven-design/buttons";
import { InputWithErrorLabel } from "components/haven-design/form/inputs";
import { getReturnToUrl } from "utils/login";
import { LoginFormValues } from "../type";
import { isEmailIdentifier } from "../utils";
import styles from "./Login.module.css";
import { loginStyles, loginAuthColumnSx } from "./styles";

const LoginForm: React.FC<{toSignUp: () => void;}> = ({ toSignUp }) => {
  const { values: { identifier, showPassword }, isSubmitting } =
    useFormikContext<LoginFormValues>();

  const isEmail = isEmailIdentifier(identifier);

  const returnToUrl = getReturnToUrl();
  const retunToCurrent = returnToUrl ? `?return-to=${returnToUrl}` : "";

  // Check if identifier looks like a phone number (contains digits and is not an email)
  const isPhoneNumber = identifier &&
    identifier.trim() &&
    !isEmail &&
    identifier.replace(/\D/g, "").length >= 9; // At least 9 digits to be considered a phone number

  // Determine button text based on login method
  const getButtonText = () => {
    if (isSubmitting) return "Loading...";
    if (isEmail) return "Log In";
    if (isPhoneNumber) return "Send Code";
    return "Log In";
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        direction="column"
        alignItems="center"
        justifyContent="center"
        size={{ xs: 12 }}
        sx={{ width: "100%" }}
      >
        <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}>
          <InputWithErrorLabel
            label="Email or Cell Phone"
            name="identifier"
            id="identifier"
            type="text"
            style={loginStyles.loginInput}
            autoFocus
          />
          {isPhoneNumber && (
            <Typography variant="body2" style={{
              textAlign: "center",
              fontSize: "0.8rem",
              color: "#666",
              marginTop: "4px",
              fontStyle: "italic",
            }}>
              We&apos;ll send a 4-digit code to your phone
            </Typography>
          )}
        </Grid>

        <Grid
          size={{ xs: 12 }}
          sx={loginAuthColumnSx}
          className={classNames({ [styles.invisibleButFillable]: !isEmail })}
        >
          <InputWithErrorLabel
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete={showPassword ? "off" : "current-password"}
            id="password"
            style={loginStyles.loginInput}
          />

          <Grid container alignItems="flex-end" justifyContent="flex-end">
            <Link
              to={`/reset-password${retunToCurrent}`}
              state={{ email: identifier }}>
              <Typography variant="body1" style={loginStyles.forgotPassword}>
                Forgot password?
              </Typography>
            </Link>
          </Grid>
        </Grid>
      </Grid>

      {isEmail && (
        <Grid
          container
          direction="column"
          alignItems="flex-start"
          justifyContent="flex-start"
          size={{ xs: 12 }}
          sx={loginAuthColumnSx}
        >
          <CheckboxWithErrorLabel
            label="Show password"
            name="showPassword"
            id="showPassword"
            style={loginStyles.showPassword}
          />
          <CheckboxWithErrorLabel
            name="remember"
            id="remember"
            label="Remember my Email"
            style={loginStyles.checkbox}
          />
        </Grid>
      )}

      <Grid
        container
        spacing={2}
        direction="column"
        alignItems="center"
        justifyContent="center"
        size={{ xs: 12 }}
        sx={{ width: "100%" }}
      >
        <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}>
          <Button
            id="login-btn"
            type="submit"
            color="primary"
            size="L"
            fullWidth
            className={styles.loginButton}
            disabled={isSubmitting}
          >
            {getButtonText()}
          </Button>
        </Grid>
        <Grid>
          <Typography variant="body2" display="inline" style={{ verticalAlign: "middle" }}>
            No account?&nbsp;
          </Typography>
          <MuiLink id="sign-up" component="button" variant="body2" display="inline" onClick={toSignUp}>Sign Up</MuiLink>
        </Grid>
      </Grid>
    </>
  );
};

export default LoginForm;
